import axios, { AxiosError, AxiosResponse, CreateAxiosDefaults } from 'axios';
import { getImpersonateQueryVariable } from '~/adapters/browser/getImpersonateQueryVariable';
import { useAuth } from '~/contexts/Auth/AuthContext';
import * as Sentry from '@sentry/react';

type CsrfMode = {
    withAccessToken?: true;
    withIdToken?: false;
    withCsrfToken: true;
};

type AccessTokenMode = {
    withAccessToken: true;
    withIdToken?: false;
    withCsrfToken?: false;
};

type IdTokenMode = {
    withAccessToken?: false;
    withIdToken: true;
    withCsrfToken?: false;
};

type AnonymousMode = {
    withAccessToken?: false;
    withIdToken?: false;
    withCsrfToken?: false;
};

export type AuthMode = CsrfMode | AccessTokenMode | IdTokenMode | AnonymousMode;

type ImpersonateOptions = { attachImpersonate: boolean };

interface CreateApiCallOptions extends ImpersonateOptions {
    auth?: AuthMode;
    headers?: CreateAxiosDefaults['headers'];
}

export type ApiCallOptions = CreateAxiosDefaults & Partial<ImpersonateOptions>;
export type ApiCallResponse<T = unknown> = AxiosResponse<T>;
export type ApiCallError = AxiosError;
export type ApiCall = <T>(url: string, options?: ApiCallOptions) => Promise<ApiCallResponse<T>>;

export const useApiCall = (baseUrl: string, createApiCallOptions: CreateApiCallOptions): ApiCall => {
    const { getTokens, identityProvider } = useAuth();

    const withCsrfToken = createApiCallOptions.auth?.withCsrfToken ?? false;
    const withAccessToken = createApiCallOptions.auth?.withAccessToken ?? false;
    const withIdToken = createApiCallOptions.auth?.withIdToken ?? false;
    let apiCallHeaders = { ...createApiCallOptions.headers };
    if (withCsrfToken) {
        apiCallHeaders = {
            ...apiCallHeaders,
            'X-CSRF-Token': `${document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content}`,
        };
    }
    const axiosInstance = axios.create();

    return async (path, options = {}) => {
        const urlWithPrefix = baseUrl + path;
        const { headers: requestHeaders, data, attachImpersonate, ...otherOptions } = options;

        const shouldAttachImpersonate = attachImpersonate ?? createApiCallOptions.attachImpersonate;
        const { params: optionParams = {} } = options;
        const params = {
            ...optionParams,
            ...{ impersonate: shouldAttachImpersonate ? getImpersonateQueryVariable() : undefined },
        };

        const { accessToken, idToken } = await getTokens();
        if (withAccessToken) {
            if (accessToken) {
                apiCallHeaders = {
                    ...apiCallHeaders,
                    Authorization: `Bearer ${accessToken}`,
                };
            } else {
                Sentry.captureMessage(`Missing access token in request to ${urlWithPrefix}`, {
                    extra: {
                        params,
                        identityProvider,
                    },
                });
            }
        } else if (withIdToken) {
            if (idToken) {
                apiCallHeaders = {
                    ...apiCallHeaders,
                    Authorization: `Bearer ${idToken}`,
                };
            } else {
                Sentry.captureMessage(`Missing id token in request to ${urlWithPrefix}`, {
                    extra: {
                        params,
                        identityProvider,
                    },
                });
            }
        }

        const requestOptions = {
            headers: { ...apiCallHeaders, ...requestHeaders },
            ...otherOptions,
        };

        return axiosInstance.request({ ...requestOptions, url: urlWithPrefix, data, params });
    };
};
